import { useCallback, useEffect, useMemo, useState } from "react";
import { PageHeader } from "../components/PageHeader";
import { fetchTimelineApprovals, updateTimelineApprovalStatus, type TimelineApprovalRecord } from "../api/subcontractorPortalApi";

function statusBadgeClass(status: TimelineApprovalRecord["status"]): string {
  switch (status) {
    case "Submitted":
      return "tl-review-modal__badge tl-review-modal__badge--submitted";
    case "Approved":
      return "tl-review-modal__badge tl-review-modal__badge--approved";
    case "Rejected":
      return "tl-review-modal__badge tl-review-modal__badge--rejected";
    case "Hold":
      return "tl-review-modal__badge tl-review-modal__badge--hold";
    default:
      return "tl-review-modal__badge";
  }
}

function statusBadgeLabel(status: TimelineApprovalRecord["status"]): string {
  switch (status) {
    case "Submitted":
      return "Pending review";
    default:
      return status;
  }
}

export function TimelineApprovalsPage() {
  const [records, setRecords] = useState<TimelineApprovalRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<"Submitted" | "Approved" | "Rejected" | "Hold">("Submitted");
  
  // Modal state for viewing photo and taking action
  const [selectedRecord, setSelectedRecord] = useState<TimelineApprovalRecord | null>(null);
  const [actionNotes, setActionNotes] = useState("");
  const [submittingAction, setSubmittingAction] = useState(false);
  const [activePhotoIdx, setActivePhotoIdx] = useState(0);

  const closeModal = useCallback(() => {
    setSelectedRecord(null);
    setActionNotes("");
    setActivePhotoIdx(0);
  }, []);

  useEffect(() => {
    setActivePhotoIdx(0);
  }, [selectedRecord?.id]);

  useEffect(() => {
    if (!selectedRecord) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeModal();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedRecord, closeModal]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchTimelineApprovals(filterStatus);
      setRecords(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load timeline approvals.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [filterStatus]);

  const handleAction = async (status: "Approved" | "Rejected" | "Hold") => {
    if (!selectedRecord) return;
    setSubmittingAction(true);
    try {
      await updateTimelineApprovalStatus(selectedRecord.id, status, actionNotes.trim() || undefined);
      // Remove or update from local list
      if (filterStatus === status) {
        setRecords(records.map(r => r.id === selectedRecord.id ? { ...r, status, status_notes: actionNotes } : r));
      } else {
        setRecords(records.filter(r => r.id !== selectedRecord.id));
      }
      closeModal();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to update status.");
    } finally {
      setSubmittingAction(false);
    }
  };

  const modalPhotos = useMemo(() => {
    if (!selectedRecord) return [] as string[];
    return [selectedRecord.photo_data, selectedRecord.photo_data_2, selectedRecord.photo_data_3].filter(Boolean) as string[];
  }, [selectedRecord]);

  const mainPhotoSrc = modalPhotos[activePhotoIdx] ?? modalPhotos[0];
  const notesReadOnly = selectedRecord?.status === "Approved" || selectedRecord?.status === "Rejected";

  return (
    <>
      <PageHeader
        breadcrumb={[{ label: "Home", to: "/" }, { label: "Timeline Approvals" }]}
        title="Timeline Approvals"
        subtitle="Review construction progress photos submitted by subcontractors from the field."
      />

      <div className="toolbar-row" style={{ marginTop: 0, marginBottom: "1rem" }}>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          {(["Submitted", "Approved", "Rejected", "Hold"] as const).map(s => (
            <button
              key={s}
              className={`btn btn--sm ${filterStatus === s ? "btn--primary btn--tg" : "btn--tg-secondary"}`}
              onClick={() => setFilterStatus(s)}
            >
              {s === "Submitted" ? "Pending Review" : s}
            </button>
          ))}
        </div>
        <button className="btn btn--sm btn--tg-secondary" onClick={() => loadData()} disabled={loading}>
          ↻ Refresh
        </button>
      </div>

      {error && <div className="alert alert--danger" style={{ marginBottom: "1rem" }}>{error}</div>}

      {loading ? (
        <div style={{ padding: "2rem", textAlign: "center", color: "#6b7280" }}>
          <span className="spinner spinner--md" style={{ marginRight: "0.5rem" }} /> Loading approvals...
        </div>
      ) : records.length === 0 ? (
        <div className="card card--elevated" style={{ padding: "3rem", textAlign: "center", color: "#6b7280" }}>
          No {filterStatus === "Submitted" ? "pending" : filterStatus.toLowerCase()} approvals found.
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: "1rem" }}>
          {records.map(record => (
            <div key={record.id} className="card card--elevated" style={{ padding: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>
              <div 
                style={{ 
                  height: "200px", 
                  backgroundColor: "#e5e7eb", 
                  backgroundImage: `url(${record.photo_data})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  cursor: "pointer"
                }}
                onClick={() => { setSelectedRecord(record); setActionNotes(record.status_notes || ""); }}
              >
                <div style={{ 
                  padding: "0.5rem", 
                  background: "linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, transparent 100%)",
                  color: "white"
                }}>
                  <h3 style={{ margin: 0, fontSize: "1rem", textShadow: "0 1px 2px rgba(0,0,0,0.5)" }}>
                    {record.phase_title}
                  </h3>
                </div>
              </div>
              <div style={{ padding: "1rem", flex: 1, display: "flex", flexDirection: "column" }}>
                <h4 style={{ margin: "0 0 0.25rem 0", fontSize: "1.05rem" }}>{record.beneficiary_name}</h4>
                <p style={{ margin: "0 0 0.5rem 0", fontSize: "0.85rem", color: "#4b5563" }}>
                  {record.village}, {record.mandal}
                </p>
                <p style={{ margin: "0 0 1rem 0", fontSize: "0.8rem", color: "#6b7280" }}>
                  By: {record.subcontractor_name}
                </p>
                <div style={{ marginTop: "auto" }}>
                  <button 
                    className="btn btn--tg btn--sm" 
                    style={{ width: "100%" }}
                    onClick={() => { setSelectedRecord(record); setActionNotes(record.status_notes || ""); }}
                  >
                    {filterStatus === "Submitted" || filterStatus === "Hold" ? "Review & Act" : "View Details"}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Review modal */}
      {selectedRecord && (
        <div
          className="modal-backdrop"
          style={{ background: "rgba(15, 23, 42, 0.72)", zIndex: 1000 }}
          onClick={closeModal}
          role="presentation"
        >
          <div
            className="tl-review-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="tl-review-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="tl-review-modal__header">
              <div className="tl-review-modal__title-block">
                <h2 id="tl-review-modal-title" className="tl-review-modal__title">
                  {selectedRecord.phase_title}
                </h2>
                <p className="tl-review-modal__meta-line">
                  <span className={statusBadgeClass(selectedRecord.status)}>{statusBadgeLabel(selectedRecord.status)}</span>
                  <span className="tl-review-modal__meta-sep" aria-hidden>
                    ·
                  </span>
                  <span>Submitted {new Date(selectedRecord.created_at).toLocaleString()}</span>
                  {selectedRecord.actual_start_date ? (
                    <>
                      <span className="tl-review-modal__meta-sep" aria-hidden>
                        ·
                      </span>
                      <span>
                        Site start <strong>{selectedRecord.actual_start_date}</strong>
                      </span>
                    </>
                  ) : null}
                </p>
              </div>
              <button type="button" className="btn btn--link tl-review-modal__close" onClick={closeModal}>
                Close
              </button>
            </header>

            <div className="tl-review-modal__body">
              <div className="tl-review-modal__grid">
                <div className="tl-review-modal__media">
                  <div className="tl-review-modal__hero">
                    {mainPhotoSrc ? (
                      <img src={mainPhotoSrc} alt={`Phase photo ${activePhotoIdx + 1}`} />
                    ) : (
                      <span style={{ color: "#94a3b8", fontSize: "0.9rem", padding: "2rem" }}>No photo attached</span>
                    )}
                  </div>
                  {modalPhotos.length > 1 ? (
                    <div className="tl-review-modal__thumbs" role="tablist" aria-label="Photos">
                      {modalPhotos.map((src, i) => (
                        <button
                          key={i}
                          type="button"
                          role="tab"
                          aria-selected={i === activePhotoIdx}
                          aria-label={`Photo ${i + 1}`}
                          className={
                            "tl-review-modal__thumb" + (i === activePhotoIdx ? " tl-review-modal__thumb--active" : "")
                          }
                          onClick={() => setActivePhotoIdx(i)}
                        >
                          <img src={src} alt="" />
                        </button>
                      ))}
                    </div>
                  ) : null}
                  {selectedRecord.video_data ? (
                    <video className="tl-review-modal__video" src={selectedRecord.video_data} controls playsInline />
                  ) : null}
                </div>

                <div className="tl-review-modal__side">
                  <div className="tl-review-modal__info-card">
                    <p className="tl-review-modal__info-label">Beneficiary</p>
                    <p className="tl-review-modal__info-name">{selectedRecord.beneficiary_name}</p>
                    <p className="tl-review-modal__info-line">
                      {selectedRecord.village}, {selectedRecord.mandal}
                      {selectedRecord.district ? ` · ${selectedRecord.district}` : ""}
                    </p>
                    <p className="tl-review-modal__info-mono">App ID: {selectedRecord.application_id}</p>
                  </div>

                  <div className="tl-review-modal__info-card">
                    <p className="tl-review-modal__info-label">Subcontractor</p>
                    <p className="tl-review-modal__info-name">{selectedRecord.subcontractor_name}</p>
                  </div>

                  {notesReadOnly ? (
                    <div className="tl-review-modal__info-card">
                      <p className="tl-review-modal__info-label">Reviewer notes</p>
                      <p className="tl-review-modal__info-line" style={{ whiteSpace: "pre-wrap", margin: 0 }}>
                        {selectedRecord.status_notes?.trim() ? selectedRecord.status_notes : "—"}
                      </p>
                    </div>
                  ) : (
                    <div className="tl-review-modal__notes tl-review-modal__info-card">
                      <label className="tl-review-modal__info-label" htmlFor="review-notes">
                        Reviewer notes (optional)
                      </label>
                      <textarea
                        id="review-notes"
                        className="input"
                        rows={4}
                        value={actionNotes}
                        onChange={(e) => setActionNotes(e.target.value)}
                        placeholder="Notes for the subcontractor or internal records…"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {(selectedRecord.status === "Submitted" || selectedRecord.status === "Hold") && (
              <footer className="tl-review-modal__footer">
                <button
                  type="button"
                  className="btn btn--danger"
                  onClick={() => void handleAction("Rejected")}
                  disabled={submittingAction}
                >
                  Reject
                </button>
                {selectedRecord.status === "Submitted" ? (
                  <button
                    type="button"
                    className="btn btn--secondary"
                    style={{ backgroundColor: "#fef3c7", borderColor: "#f59e0b", color: "#b45309" }}
                    onClick={() => void handleAction("Hold")}
                    disabled={submittingAction}
                  >
                    Hold
                  </button>
                ) : null}
                <button
                  type="button"
                  className="btn btn--primary btn--tg"
                  style={{ backgroundColor: "#15803d", borderColor: "#15803d" }}
                  onClick={() => void handleAction("Approved")}
                  disabled={submittingAction}
                >
                  Approve phase
                </button>
              </footer>
            )}
          </div>
        </div>
      )}
    </>
  );
}
